import type {
  ActivityType,
  ClinicalEventType,
  PatientEventType,
} from '@/services/activityLogService';
import type {
  GaitParameterKey,
  PerceptionType,
  STSParameterKey,
  TUGParameterKey,
} from '@/types/measurements';
import type { QuestionnaireType } from '@/types/questionnaire';

import {
  enQuestionnaireTranslations,
  questionnaireNameLabels,
} from './enQuestionnaireTranslations';

type EnActivityLogPageTranslations = {
  activityLogPage: {
    activityLog: {
      [K in PerceptionType]?: any;
    } & {
      labels: {};
      addEvent: string;
      addNamedEvent: string;
      clinicalEvent: {
        surgery: string;
        fall: string;
        deviceAdjustment: string;
        surgeryDate: string;
        fallDate: string;
        adjustmentDate: string;
        surgeryType: string;
        otherSurgeryType: string;
        deviceType: string;
        edit: string;
        noDetails: string;
        saveFailed: string;
        deleteFailed: string;
        adjustmentTime: string;
        cancel: string;
        saveEvent: string;
        select: string;
        selectType: string;
        addTitle: { surgery: string; fall: string; deviceAdjustment: string };
        editTitle: { surgery: string; fall: string; deviceAdjustment: string };
        witnessed: string;
        location: string;
        comments: string;
        description: string;
        yes: string;
        no: string;
      };
      surgery: { name: string };
      fall: { name: string };
      device_adjustment: { name: string };
      disabledActivityTooltip: string;
      delete: {
        dialog: {
          title: string;
          body: string;
          subBody: string;
          confirm: string;
          cancel: string;
          questionnaireTitle: string;
          questionnaireBody: string;
          eventTitle: string;
          eventBody: string;
        };
        toast: {
          success: string;
          error: string;
          questionnaireSuccess: string;
          questionnaireError: string;
        };
      };
    } & { questionnaire?: any } & { [K in PatientEventType]?: any };
    contextPanel: {
      header: {
        title: Partial<
          Record<PerceptionType | QuestionnaireType | PatientEventType | ClinicalEventType, string>
        >;
        titleInfo: Partial<Record<ActivityType | 'questionnaire', string>>;
      };
      detailsTable: {
        mostRecent: string;
        comparedTo: string;
        noPreviousMeasurements: string;
        selectedActivity: string;
        parameter: string;
        range: string;
        notes: string;
        noTags: string;
        tagLabels: Record<string, string>;
        generalDataKeys: Record<string, string>;
        perceptionParameters: {
          [K in PerceptionType]?: Partial<
            Record<TUGParameterKey | STSParameterKey | GaitParameterKey, string>
          >;
        };
      };
      staticBalance: {
        partialLoadError: string;
      };
    };
    exportToExcel: string;
    emptyFilterState: {};
    emptyState: {};
  };
};

export const enActivityLogPageTranslations: EnActivityLogPageTranslations = {
  activityLogPage: {
    activityLog: {
      labels: { partial: 'Partial analysis', minimal: 'Minimal analysis', no_result: 'No result' },
      addEvent: 'Add event',
      addNamedEvent: 'Add {{name}}',
      clinicalEvent: {
        surgery: 'Surgery',
        fall: 'Fall',
        deviceAdjustment: 'Device adjustment',
        surgeryDate: 'Surgery date',
        fallDate: 'Fall date',
        adjustmentDate: 'Adjustment date',
        surgeryType: 'Surgery type',
        otherSurgeryType: 'Other surgery type',
        deviceType: 'Device type',
        edit: 'Edit',
        noDetails: 'No further details were recorded.',
        saveFailed: 'Failed to save the event',
        deleteFailed: 'Failed to delete the event',
        adjustmentTime: 'Adjustment time',
        cancel: 'Cancel',
        saveEvent: 'Save event',
        select: 'Select',
        selectType: 'Select type',
        addTitle: {
          surgery: 'Add surgery event',
          fall: 'Add fall event',
          deviceAdjustment: 'Add Device adjustment',
        },
        editTitle: {
          surgery: 'Edit surgery event',
          fall: 'Edit fall event',
          deviceAdjustment: 'Edit Device adjustment',
        },
        witnessed: 'Witnessed',
        location: 'Location',
        comments: 'Comments',
        description: 'Description',
        yes: 'Yes',
        no: 'No',
      },
      surgery: { name: 'Surgery' },
      fall: { name: 'Fall' },
      device_adjustment: { name: 'Device adjustment' },
      disabledActivityTooltip: 'No measurements of this type are available',
      delete: {
        dialog: {
          title: 'Delete measurement',
          body: 'This action cannot be undone. Deleting this measurement may affect motion measurement features, including billing reports.',
          subBody:
            'Previously generated reports will not be updated automatically. Review your EMR if needed.',
          confirm: 'Delete',
          cancel: 'Cancel',
          eventTitle: 'Delete {{name}} event',
          eventBody:
            'This action cannot be undone. The event will be removed from the activity log.',
          questionnaireTitle: 'Delete questionnaire',
          questionnaireBody:
            'This action cannot be undone. Deleting this questionnaire may affect billing reports.',
        },
        toast: {
          success: 'Measurement discarded',
          error: 'Failed to discard measurement',
          questionnaireSuccess: 'Questionnaire discarded',
          questionnaireError: 'Failed to discard questionnaire',
        },
      },
      walk: {
        name: 'Active Walk',
        valueLabel: 'Score: {{value}}',
        data: {
          duration: 'Duration: {{duration}} sec',
        },
      },
      bg_walk: {
        name: 'Passive Walk',
        valueLabel: 'Score: {{value}}',
        dailyTimeLabel: 'Daily Average',
      },
      dtw: {
        name: 'Dual Task',
        valueLabel: 'Score: {{value}}',
      },
      daily_steps: {
        name: 'Daily Steps',
        valueLabel: '{{value, number}} steps',
      },
      tug: { name: 'Timed Up and Go', valueLabel: 'Duration: {{value}} sec' },
      sts: { name: 'Sit to Stand', valueLabel: 'Score: {{value}} repetitions' },
      '6mw': { name: '6 Minute Walk Test', valueLabel: 'Distance: {{value}} {{unit}}' },
      '2mw': { name: '2 Minute Walk Test', valueLabel: 'Distance: {{value}} {{unit}}' },
      functionalReach: { name: 'Functional Reach', valueLabel: 'Distance: {{value}} {{unit}}' },
      static_balance_test: {
        name: 'Static Balance',
        valueLabel: 'Score: {{value}}',
        data: {
          duration: 'Duration: {{duration}} sec',
        },
      },
      patient_event: {
        name: 'Event',
        valueLabel: '{{value}}',
      },
      questionnaire: {
        name: 'Questionnaire',
        valueLabel: 'Score: {{value}}',
        valueLabelWithMax: 'Score: {{value}} of {{maxScore}}',
        summaryLabel: '<bold>Summary:</bold> {{value}}',
        kLevelLabel: 'This score is associated with K level {{kLevel}}.',
        ...enQuestionnaireTranslations,
      },
    },

    contextPanel: {
      header: {
        title: {
          walk: 'Active Walk',
          sts: 'Sit to Stand',
          tug: 'Timed up and Go',
          bg_walk: 'Passive Walk',
          dtw: 'Dual Task',
          daily_steps: 'Daily Steps',
          '6mw': '6 Minute Walk Test',
          '2mw': '2 Minute Walk Test',
          functionalReach: 'Functional Reach',
          static_balance_test: 'Static Balance',
          patient_event: 'Event',
          surgery: 'Event',
          fall: 'Event',
          device_adjustment: 'Event',
          ...questionnaireNameLabels,
        },
        titleInfo: {
          walk: '{{date}}',
          sts: '{{date}}',
          tug: '{{date}}',
          bg_walk: '',
          dtw: '{{date}}',
          daily_steps: '{{date}}',
          '6mw': '{{date}}',
          '2mw': '{{date}}',
          functionalReach: '{{date}}',
          static_balance_test: '{{date}}',
          patient_event: '{{date}}',
          questionnaire: '{{date}}',
        },
      },
      detailsTable: {
        comparedTo: 'Compared to',
        noPreviousMeasurements: 'No previous measurements',
        selectedActivity: 'Selected activity',
        parameter: 'Parameter',
        range: 'Range',
        notes: 'Notes',
        mostRecent: '(previous)',
        noTags: 'No tags',
        generalDataKeys: {
          steps: 'Steps',
          seconds: 'Duration (sec)',
          tags: 'Tags',
        },
        tagLabels: {
          useofhands: 'Used hands',
        },
        perceptionParameters: {
          tug: {
            tug_duration_seconds: 'Duration',
            tug_forward_seconds: 'Forward time',
            tug_backward_seconds: 'Backward time',
            tug_sitting_seconds: 'Sitting time',
            tug_standing_seconds: 'Standing time',
            tug_turning_seconds: 'Turn time',
            tug_turning_to_chair_seconds: 'Turn-to-chair time',
            tug_distance_meters: 'Distance',
          },
          sts: {
            sts_repetition_count: 'Number of repetitions',
            sts_repetition_time: 'Average repetition time',
            sts_repetition_var: 'Repetition variance',
            sts_angle: 'Average angle',
          },
        },
      },
      staticBalance: {
        partialLoadError: "Some balance tests couldn't be loaded. Please try again.",
      },
    },
    exportToExcel: 'Export walks to excel',
    emptyFilterState: {
      title: 'No measurements taken',
      description:
        'Try adjusting the filters, expanding the date range, or turning on passive recordings to see more',
    },
    emptyState: {
      title: 'No measurements taken',
      description: 'Start taking measurements to assess fall risk and track progress over time',
    },
  },
};
